/**
 * SIP-030 `request()` adapter for this app's contract-call / contract-deploy sites.
 *
 * Stacks Connect v8 still exports `openContractCall` / `openContractDeploy`, but only as
 * `@deprecated` wrappers that translate to `request()` internally. These helpers call
 * `request()` directly so the app no longer depends on that deprecated surface.
 *
 * The option shape below is deliberately the *legacy* one (`contractAddress` + `contractName`,
 * a `StacksNetwork` object, numeric `PostConditionMode`, `onFinish` / `onCancel` callbacks).
 * Keeping it means every existing call site changes by one identifier and no caller anywhere
 * in the app has to change. Converting this app's ~28 callback-style helpers to promises is a
 * separate refactor with a much larger blast radius, and is not required by Stacks.js.
 *
 * Fields that SIP-030 has no equivalent for (`appDetails`, `anchorMode`, `userSession`) are
 * accepted and ignored — the wallet now owns app identity, anchor mode and account selection.
 */
import { request, JsonRpcError, JsonRpcErrorCode } from '@stacks/connect';
import { PostConditionMode } from '@stacks/transactions';
import type { ClarityValue, PostCondition } from '@stacks/transactions';
import type { StacksNetwork } from '@stacks/network';
import { WALLET_SELECTION_OPTIONS } from './stacksAuth';

/** What the wallet hands back, shaped like the legacy `FinishedTxData` this app already reads. */
export type RequestFinishedTxData = {
  txId: string;
  txRaw: string;
};

type NetworkLike = StacksNetwork | string | undefined;
type PostConditionModeLike = PostConditionMode | 'allow' | 'deny' | 'originator' | number;

type CommonLegacyOptions = {
  network?: NetworkLike;
  postConditions?: (string | PostCondition)[];
  postConditionMode?: PostConditionModeLike;
  fee?: number | string;
  nonce?: number | string;
  sponsored?: boolean;
  onFinish?: (data: RequestFinishedTxData) => void;
  onCancel?: (error?: Error) => void;
  /** Accepted for source compatibility and ignored — not part of SIP-030. */
  appDetails?: unknown;
  anchorMode?: unknown;
  userSession?: unknown;
};

export type RequestContractCallOptions = CommonLegacyOptions & {
  contractAddress: string;
  contractName: string;
  functionName: string;
  functionArgs?: ClarityValue[];
};

export type RequestContractDeployOptions = CommonLegacyOptions & {
  contractName: string;
  codeBody: string;
  clarityVersion?: number | string;
};

/**
 * v7 networks are plain objects, but `request()` takes a network *name*. Discriminate on
 * `chainId` (mainnet 1, testnet 0x80000000) rather than on object identity, so a network
 * carrying a custom `client.baseUrl` still resolves correctly.
 */
export function networkNameOf(network: NetworkLike): 'mainnet' | 'testnet' {
  if (typeof network === 'string') {
    return network === 'mainnet' ? 'mainnet' : 'testnet';
  }
  return network?.chainId === 1 ? 'mainnet' : 'testnet';
}

/** Numeric `PostConditionMode` -> the string form `request()` expects. */
export function postConditionModeNameOf(
  mode: PostConditionModeLike | undefined,
): 'allow' | 'deny' | undefined {
  if (mode === undefined) return undefined;
  if (mode === 'allow' || mode === 'deny') return mode;
  if (mode === 'originator') return 'deny';
  return mode === PostConditionMode.Allow ? 'allow' : 'deny';
}

/** True when the user dismissed the wallet rather than the request genuinely failing. */
export function isUserCancellation(error: unknown): boolean {
  const code = (error as JsonRpcError | undefined)?.code;
  return code === JsonRpcErrorCode.UserRejection || code === JsonRpcErrorCode.UserCanceled;
}

function toFinishedTxData(result: { txid?: string; transaction?: string }): RequestFinishedTxData {
  // The legacy shim mapped `txId: e.txid` with no normalisation, so this is byte-faithful to it.
  // It notably does not add or strip a `0x` prefix — call sites that build
  // `explorer.stacks.co/txid/0x${txid}` inherit exactly the behaviour they had before.
  return { txId: result.txid ?? '', txRaw: result.transaction ?? '' };
}

/**
 * Drop keys whose value is `undefined` so they are never transmitted. Most call sites leave
 * `postConditions`, `fee`, `nonce`, `sponsored` and `clarityVersion` unset, and an explicitly
 * present `undefined` is not the same thing as an absent key to a validating wallet.
 */
function compact<T extends Record<string, unknown>>(params: T): T {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as T;
}

/**
 * Dispatch the callbacks. Never rejects: a rejected promise here would surface as an
 * unhandled rejection, since the legacy call sites do not await.
 */
function settle(
  promise: Promise<{ txid?: string; transaction?: string }>,
  onFinish?: (data: RequestFinishedTxData) => void,
  onCancel?: (error?: Error) => void,
): Promise<void> {
  return promise.then(
    (result) => {
      onFinish?.(toFinishedTxData(result));
    },
    (error) => {
      if (isUserCancellation(error)) {
        onCancel?.(error as Error);
        return;
      }
      console.error('Stacks request failed', error);
      // The legacy shim routed every non-success outcome to onCancel, so preserve that:
      // call sites rely on it to close modals and reset button state.
      onCancel?.(error as Error);
    },
  );
}

/** Drop-in replacement for the deprecated `openContractCall`. */
export function requestContractCall(options: RequestContractCallOptions): Promise<void> {
  const { contractAddress, contractName, functionName, functionArgs, onFinish, onCancel } = options;

  return settle(
    request(
      // Wallet allowlist is a per-request option in v8; omitting it here would let an
      // unapproved wallet appear at this call site even though connect() is restricted.
      WALLET_SELECTION_OPTIONS,
      'stx_callContract',
      compact({
        contract: `${contractAddress}.${contractName}` as `${string}.${string}`,
        functionName,
        functionArgs: functionArgs ?? [],
        network: networkNameOf(options.network),
        postConditions: options.postConditions,
        postConditionMode: postConditionModeNameOf(options.postConditionMode),
        fee: options.fee,
        nonce: options.nonce,
        sponsored: options.sponsored,
      }),
    ),
    onFinish,
    onCancel,
  );
}

/** Drop-in replacement for the deprecated `openContractDeploy`. */
export function requestContractDeploy(options: RequestContractDeployOptions): Promise<void> {
  const { contractName, codeBody, onFinish, onCancel } = options;

  return settle(
    request(
      WALLET_SELECTION_OPTIONS,
      'stx_deployContract',
      compact({
        name: contractName,
        clarityCode: codeBody,
        network: networkNameOf(options.network),
        postConditions: options.postConditions,
        postConditionMode: postConditionModeNameOf(options.postConditionMode),
        fee: options.fee,
        nonce: options.nonce,
        sponsored: options.sponsored,
      }),
    ),
    onFinish,
    onCancel,
  );
}
